import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Globe, Link2, Lock, Copy, Check, RefreshCw, Trash2, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL?.replace('/api', '').replace(/\/$/, '') || window.location.origin;

const VISIBILITY = [
  { value: 'private', label: 'Private', icon: Lock, desc: 'Only visible to you' },
  { value: 'link', label: 'Shareable with link', icon: Link2, desc: 'Anyone with the link can view' },
  { value: 'public', label: 'Public', icon: Globe, desc: 'Visible to everyone' },
];

const CollectionDialog = ({ open, onOpenChange, collection, onSaved }) => {
  const isEdit = !!collection?.id;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setName(collection?.name || '');
      setDescription(collection?.description || '');
      setVisibility(collection?.visibility || 'private');
      setError('');
      setCopied(false);
    }
  }, [open, collection]);

  const shareUrl = collection?.share_id ? `${BASE}/collection/${collection.share_id}` : '';

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        const res = await axios.patch(`${API}/collections/${collection.id}`, { name: name.trim(), description: description.trim(), visibility });
        onSaved?.(res.data);
      } else {
        const res = await axios.post(`${API}/collections`, { name: name.trim(), description: description.trim(), visibility, product_titles: [] });
        onSaved?.(res.data);
      }
      onOpenChange(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save the collection');
    }
    setSaving(false);
  };

  const handleRegenerate = async () => {
    try {
      const res = await axios.post(`${API}/collections/${collection.id}/share`);
      const updated = { ...collection, share_id: res.data.share_id, visibility: 'link' };
      onSaved?.(updated);
      onOpenChange(false);
    } catch { setError('Could not generate new link'); }
  };

  const handleDeactivate = async () => {
    try {
      await axios.delete(`${API}/collections/${collection.id}/share`);
      const updated = { ...collection, share_id: null, visibility: 'private' };
      onSaved?.(updated);
      onOpenChange(false);
    } catch { setError('Could not deactivate link'); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); } catch {
      const el = document.createElement('input'); el.value = shareUrl;
      document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el);
    }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="collection-dialog" aria-describedby="collection-dialog-desc">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-stone-900">
            {isEdit ? 'Edit collection' : 'New collection'}
          </DialogTitle>
          <p className="font-sans text-sm text-stone-500" id="collection-dialog-desc">{isEdit ? 'Change settings for the collection.' : 'Create a curated collection of products.'}</p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1 block">Navn</label>
            <input
              data-testid="collection-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Wedding Favorites"
              maxLength={100}
              className="w-full px-3 py-2.5 border border-stone-200 rounded-xl font-sans text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:border-[#B49A88]"
            />
          </div>

          <div>
            <label className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1 block">Description</label>
            <textarea
              data-testid="collection-desc-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short description of the collection..."
              maxLength={500}
              rows={2}
              className="w-full px-3 py-2.5 border border-stone-200 rounded-xl font-sans text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:border-[#B49A88] resize-none"
            />
          </div>

          <div>
            <label className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1.5 block">Visiblehet</label>
            <div className="space-y-2">
              {VISIBILITY.map((v) => (
                <button
                  key={v.value}
                  data-testid={`visibility-${v.value}`}
                  onClick={() => setVisibility(v.value)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all ${
                    visibility === v.value
                      ? 'border-[#C49A6C] bg-[#B49A88]/5'
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                >
                  <v.icon className={`w-4 h-4 flex-shrink-0 ${visibility === v.value ? 'text-[#B49A88]' : 'text-stone-400'}`} />
                  <div>
                    <p className="font-sans text-sm font-medium text-stone-700">{v.label}</p>
                    <p className="font-sans text-xs text-stone-400">{v.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Share link section (edit mode only, when shareable) */}
          {isEdit && collection?.share_id && visibility !== 'private' && (
            <div className="pt-2 border-t border-stone-100">
              <label className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1.5 block">Share link</label>
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 text-xs font-mono text-stone-600 truncate" data-testid="collection-share-url">
                  {shareUrl}
                </div>
                <Button onClick={copyLink} variant="outline" className="flex-shrink-0 rounded-xl h-9 px-2.5 border-stone-200" data-testid="copy-collection-link">
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-stone-500" />}
                </Button>
                <Button onClick={() => window.open(shareUrl, '_blank')} variant="outline" className="flex-shrink-0 rounded-xl h-9 px-2.5 border-stone-200">
                  <ExternalLink className="w-4 h-4 text-stone-500" />
                </Button>
              </div>
              {copied && <p className="font-sans text-xs text-green-600 mb-2">Link copied!</p>}
              <div className="flex items-center gap-2">
                <button onClick={handleRegenerate} className="flex items-center gap-1 font-sans text-xs text-stone-400 hover:text-primary transition-colors" data-testid="regenerate-link">
                  <RefreshCw className="w-3 h-3" /> Generer ny link
                </button>
                <span className="text-stone-200">|</span>
                <button onClick={handleDeactivate} className="flex items-center gap-1 font-sans text-xs text-stone-400 hover:text-red-500 transition-colors" data-testid="deactivate-link">
                  <Trash2 className="w-3 h-3" /> Deaktiver
                </button>
              </div>
            </div>
          )}

          {error && <p data-testid="collection-error" className="font-sans text-xs text-red-500">{error}</p>}
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button
            data-testid="save-collection-btn"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-[#B49A88] hover:bg-[#B49A88] text-white rounded-full py-5 font-sans font-semibold"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {isEdit ? 'Save changes' : 'Create collection'}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="font-sans text-stone-500">Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CollectionDialog;
